import styles from './PipelineFunnel.module.css'

/**
 * Pipeline funnel — horizontal bar waterfall showing where products
 * are lost at each stage of the scraping pipeline.
 *
 * Stages: Extracted → Classified → Accepted → Normalized → Published
 * (Fetched is shown as metadata, not a bar — it's page count, not product count)
 *
 * @param {{ source: object, sloViolations?: string[] }} props
 */
export function PipelineFunnel({ source }) {
  const {
    fetched = 0,
    extracted = 0,
    classified = 0,
    accepted = 0,
    normalized = 0,
    droppedInvalid = 0,
    rejected = 0,
    durationMs = 0,
    pipelineStatus,
    sloViolations = [],
  } = source

  // Published = normalized minus any SLO-dropped items
  // (If the source was retained/failed, none were published from this run)
  const published = (pipelineStatus === 'success' || pipelineStatus === 'degraded')
    ? normalized
    : 0

  const stages = [
    {
      label: 'Extracted',
      value: extracted,
      drop: null,
      color: 'var(--admin-text)',
      desc: 'Raw products parsed from page',
    },
    {
      label: 'Classified',
      value: classified,
      drop: extracted - classified,
      dropLabel: 'no bio marker',
      color: 'var(--admin-info)',
      desc: 'Sent to classification pipeline',
    },
    {
      label: 'Accepted',
      value: accepted,
      drop: classified - accepted,
      dropLabel: 'rejected by classifier',
      color: 'var(--admin-accent)',
      desc: 'Passed classification as bio produce',
    },
    {
      label: 'Normalized',
      value: normalized,
      drop: accepted - normalized - droppedInvalid,
      dropLabel: 'dropped in mapping',
      color: 'var(--funnel-normalized)',
      desc: 'Valid offers after normalization',
    },
  ]

  // Only show droppedInvalid as a separate line if > 0
  if (droppedInvalid > 0) {
    stages.push({
      label: 'Validated',
      value: normalized,
      drop: droppedInvalid,
      dropLabel: 'failed validation',
      color: 'var(--funnel-validated)',
      desc: 'Passed offer contract check',
    })
  }

  // Add published stage if SLO gate blocked any
  if (sloViolations.length > 0 || published !== normalized) {
    stages.push({
      label: 'Published',
      value: published,
      drop: normalized - published,
      dropLabel: 'blocked by SLO',
      color: 'var(--admin-success)',
      desc: 'Promoted to live offers',
      isFinal: true,
    })
  }

  // Max value for bar scaling
  const maxValue = Math.max(extracted, 1)

  return (
    <div className={styles.funnel}>
      <div className={styles.meta}>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Fetched</span>
          <span className={styles.metaValue}>{fetched} {fetched === 1 ? 'page' : 'pages'}</span>
        </span>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Duration</span>
          <span className={styles.metaValue}>{formatDurationMs(durationMs)}</span>
        </span>
        {pipelineStatus && (
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>Pipeline</span>
            <span className={`${styles.metaValue} ${styles[`status_${pipelineStatus}`] || ''}`}>
              {pipelineStatus}
            </span>
          </span>
        )}
      </div>

      <div className={styles.stages}>
        {stages.map((stage, i) => {
          const pct = maxValue > 0 ? (stage.value / maxValue) * 100 : 0
          const isLast = i === stages.length - 1

          return (
            <div key={stage.label} className={styles.stageRow}>
              {/* Drop indicator between stages */}
              {stage.drop > 0 && (
                <div className={styles.dropRow}>
                  <div className={styles.dropConnector} />
                  <span className={styles.dropLabel}>
                    −{stage.drop} {stage.dropLabel}
                  </span>
                </div>
              )}

              <div className={styles.stage}>
                <span className={styles.stageLabel}>{stage.label}</span>
                <div className={styles.barTrack}>
                  <div
                    className={`${styles.barFill} ${isLast && stage.isFinal ? styles.barFinal : ''}`}
                    style={{
                      '--bar-width': `${Math.max(pct, 0.5)}%`,
                      '--bar-color': stage.color,
                    }}
                  />
                </div>
                <span className={styles.stageCount}>{stage.value}</span>
              </div>
            </div>
          )
        })}
      </div>

      {sloViolations.length > 0 && (
        <div className={styles.violations}>
          <span className={styles.violationsTitle}>SLO violations</span>
          <ul className={styles.violationsList}>
            {sloViolations.map((v, i) => (
              <li key={i} className={styles.violation}>{v}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function formatDurationMs(ms) {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}
